import React, { memo } from 'react';
import { useSelector } from 'react-redux';

import { Chip, Divider, Typography } from '@mui/material';
import { FormNames } from 'app/services/autoLockForms/autoLockForms.constants';
import { TextFieldCDS } from 'app/shared/componentsRestyled/coreDesignSystem/form';
import PropType from 'prop-types';

import { KEY_CODES } from 'constants/keyboard';
import { getPressedKey } from 'utils/keyboard';

import { selectUserData } from 'store/auth/selectors';
import { selectFormLockedFieldsMap, selectFormLockedPage } from 'store/loanApplications/selectors';

import { ActivitiesTestIds } from '../../../Activities.testIds';
import { MAX_SUBJECT_LENGTH } from '../../EmailCreationModal.config';

import RecipientsFields from './RecipientsFields';

const preventSubmitHandler = e => {
  if (getPressedKey(e) === KEY_CODES.ENTER) {
    e.preventDefault();
  }
};

const textInputProps = { disableUnderline: true };

const NewEmailFields = ({
  isLoading,
  form,
  validationErrors,
  setInForm,
  validateField,
  onChange,
  shouldExpandCc = false,
  onRecipientFieldErrorsChange,
}) => {
  const senderData = useSelector(selectUserData);
  const lockedFieldsMap = useSelector(selectFormLockedFieldsMap(FormNames.ACTIVITIES_EMAILS));
  const isPageLocked = useSelector(selectFormLockedPage(FormNames.ACTIVITIES_EMAILS));

  const isDisabledByLock = isLoading || isPageLocked;

  return (
    <div className="px-24 py-6 space-y-3">
      <RecipientsFields
        form={form}
        isLoading={isLoading}
        preventSubmitHandler={preventSubmitHandler}
        setInForm={setInForm}
        shouldExpandCc={shouldExpandCc}
        validateField={validateField}
        validationErrors={validationErrors}
        onRecipientFieldErrorsChange={onRecipientFieldErrorsChange}
      />

      <Divider />

      <div className="flex items-center min-h-32">
        <Typography className="mr-12" variant="regular14">
          From:
        </Typography>
        <Chip label={`${senderData.displayName} <${senderData.email}>`} size="medium" />
      </div>

      <Divider />

      <div className="flex items-baseline min-h-32">
        <Typography className="mr-12" variant="regular14">
          Subject:
        </Typography>
        <TextFieldCDS
          fullWidth
          InputProps={textInputProps}
          autoComplete="off"
          dataTestId={ActivitiesTestIds.SUBJECT_INPUT}
          disabled={isDisabledByLock || lockedFieldsMap.subject}
          error={!!validationErrors.subject}
          helperText={validationErrors.subject}
          maxLength={MAX_SUBJECT_LENGTH}
          name="subject"
          value={form.subject}
          variant="standard"
          onChange={onChange}
          onKeyDown={preventSubmitHandler}
        />
      </div>
    </div>
  );
};

NewEmailFields.propTypes = {
  isLoading: PropType.bool.isRequired,
  form: PropType.object.isRequired,
  validationErrors: PropType.object.isRequired,
  setInForm: PropType.func.isRequired,
  validateField: PropType.func.isRequired,
  onChange: PropType.func.isRequired,
  shouldExpandCc: PropType.bool,
  onRecipientFieldErrorsChange: PropType.func,
};

export default memo(NewEmailFields);
